import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Card, CardContent, Grid, Typography, CircularProgress } from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";

import AppTable from "../../common/app-table";
import AppDropdown from "../../common/app-dropdown";
import StatTile from "../../common/stat-tile";
import TableSearchInput from "../TableSearchInput";
import { ColorsConstants, grayFontColor } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { resolveSearchQueryForApi } from "../../utils/phoneSearch";
import {
  REACH_FREQUENCY_CAMPAIGNS_ENDPOINT,
  REACH_FREQUENCY_CARDS_ENDPOINT,
  REACH_FREQUENCY_CHART_ENDPOINT,
  REACH_FREQUENCY_TABLE_ENDPOINT,
} from "../../config/urls";

const BUCKET_ORDER = ["0", "1-3", "4-10", "10+"];
const BUCKET_COLORS = {
  "0": ColorsConstants.primaryBlue70,
  "1-3": ColorsConstants.primaryBlue80,
  "4-10": ColorsConstants.secondaryBackgroundColor,
  "10+": ColorsConstants.error,
};

const SUMMARY_CARD_SX = { backgroundColor: "#F8F9FB", border: "1px solid #E4E7EC" };

function formatCardValue(value) {
  return typeof value === "number" ? value.toLocaleString() : value;
}

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

const columns = [
  { id: "phone", label: "Phone" },
  {
    id: "touches",
    label: "Touches",
    align: "right",
    sortable: true,
    render: (row) => (
      <Typography
        sx={{
          ...typography.bodyLarge,
          color: row.touches >= 10 ? ColorsConstants.error : "inherit",
          fontWeight: row.touches >= 10 ? 600 : 400,
        }}
      >
        {row.touches}
      </Typography>
    ),
  },
  { id: "sent", label: "Sent", align: "right" },
  { id: "delivered", label: "Delivered", align: "right" },
  { id: "read_count", label: "Read", align: "right", render: (row) => row.read_count },
  { id: "replied", label: "Replied", align: "right", render: (row) => row.replied ?? "—" },
  { id: "failed", label: "Failed", align: "right" },
  {
    id: "last_touch",
    label: "Last touch",
    render: (row) => (row.last_touch ? dayjs(row.last_touch).format("MMM D") : "—"),
  },
];

const ReachFrequencyReport = () => {
  const { getValidToken } = useAuth();

  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState(null); // null = "All campaigns"

  const [cards, setCards] = useState(null);
  const [loadingCards, setLoadingCards] = useState(false);
  const [cardsError, setCardsError] = useState(false);

  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [chartError, setChartError] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 400);
  const resolvedSearch = useMemo(
    () => resolveSearchQueryForApi(debouncedSearch),
    [debouncedSearch],
  );

  const [page, setPage] = useState(0); // 0-indexed for AppTable/MUI
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortState, setSortState] = useState([{ id: "touches", direction: "desc" }]);
  const [tableRows, setTableRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loadingTable, setLoadingTable] = useState(false);

  const getHeaders = useCallback(async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [getValidToken]);

  // Campaign dropdown options — loaded once
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const headers = await getHeaders();
        const response = await fetch(REACH_FREQUENCY_CAMPAIGNS_ENDPOINT, { headers });
        if (!response.ok) throw new Error("Failed to fetch campaigns");
        const data = await response.json();
        if (isMounted) setCampaigns(Array.isArray(data) ? data : data.results || []);
      } catch (error) {
        console.error("Error fetching reach & frequency campaigns:", error.message);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [getHeaders]);

  // Summary cards
  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoadingCards(true);
      setCardsError(false);
      try {
        const headers = await getHeaders();
        const params = new URLSearchParams();
        if (campaignId) params.set("campaign_id", campaignId);
        const response = await fetch(`${REACH_FREQUENCY_CARDS_ENDPOINT}?${params.toString()}`, { headers });
        if (!response.ok) throw new Error("Failed to fetch summary cards");
        const data = await response.json();
        if (isMounted) setCards(data);
      } catch (error) {
        console.error("Error fetching reach & frequency cards:", error.message);
        if (isMounted) {
          setCards(null);
          setCardsError(true);
        }
      } finally {
        if (isMounted) setLoadingCards(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [campaignId, getHeaders]);

  // Distribution chart
  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoadingChart(true);
      setChartError(false);
      try {
        const headers = await getHeaders();
        const params = new URLSearchParams();
        if (campaignId) params.set("campaign_id", campaignId);
        const response = await fetch(`${REACH_FREQUENCY_CHART_ENDPOINT}?${params.toString()}`, { headers });
        if (!response.ok) throw new Error("Failed to fetch distribution chart");
        const data = await response.json();
        if (isMounted) setChartData(Array.isArray(data) ? data : data.results || []);
      } catch (error) {
        console.error("Error fetching reach & frequency chart:", error.message);
        if (isMounted) {
          setChartData([]);
          setChartError(true);
        }
      } finally {
        if (isMounted) setLoadingChart(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [campaignId, getHeaders]);

  // Per-contact detail table
  useEffect(() => {
    if (resolvedSearch.blockFetch) {
      setTableRows([]);
      setTotalRows(0);
      return;
    }
    let isMounted = true;
    (async () => {
      setLoadingTable(true);
      try {
        const headers = await getHeaders();
        const params = new URLSearchParams();
        if (campaignId) params.set("campaign_id", campaignId);
        if (resolvedSearch.apiSearchQuery) params.set("search", resolvedSearch.apiSearchQuery);
        const activeSort = sortState[0];
        params.set("sort", activeSort ? `${activeSort.direction === "desc" ? "-" : ""}${activeSort.id}` : "-touches");
        // limit/offset, matching the convention used by AppTable elsewhere (e.g. TemplateAnalyticsTable).
        params.set("limit", String(rowsPerPage));
        params.set("offset", String(page * rowsPerPage));

        const response = await fetch(`${REACH_FREQUENCY_TABLE_ENDPOINT}?${params.toString()}`, { headers });
        if (!response.ok) throw new Error("Failed to fetch per-contact detail");
        const data = await response.json();
        if (isMounted) {
          setTableRows(data.results || []);
          setTotalRows(data.count || 0);
        }
      } catch (error) {
        console.error("Error fetching reach & frequency table:", error.message);
        if (isMounted) {
          setTableRows([]);
          setTotalRows(0);
        }
      } finally {
        if (isMounted) setLoadingTable(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [campaignId, resolvedSearch, sortState, page, rowsPerPage, getHeaders]);

  // Reset to first page whenever filters change underneath the table
  useEffect(() => {
    setPage(0);
  }, [campaignId, resolvedSearch.apiSearchQuery, sortState]);

  const handleSortChange = (columnId) => {
    setSortState((prev) => {
      const current = prev[0];
      if (current?.id === columnId) {
        return [{ id: columnId, direction: current.direction === "desc" ? "asc" : "desc" }];
      }
      return [{ id: columnId, direction: "desc" }];
    });
  };

  const campaignMenuItems = [
    { label: "All campaigns (aggregate)", value: "" },
    ...campaigns
      .filter((c) => c.id !== null && c.id !== undefined)
      .map((c) => ({ label: c.campaign_name, value: c.id })),
  ];
  const selectedCampaignLabel =
    campaignMenuItems.find((item) => item.value === (campaignId ?? ""))?.label ||
    "All campaigns (aggregate)";

  const orderedChartData = BUCKET_ORDER.map(
    (bucket) => chartData.find((d) => d.bucket === bucket) || { bucket, contact_count: 0 },
  );

  return (
    <Box sx={{ mt: 3, mb: 1, mx: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ ...typography.h1, fontWeight: 500, color: ColorsConstants.primaryBlue100 }}>
          Reach & Frequency Report
        </Typography>
      </Box>

      {/* Campaign filter */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>Campaign</Typography>
        <AppDropdown
          endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
          menuItems={campaignMenuItems}
          selectedValue={campaignId ?? ""}
          onMenuSelect={(value) => setCampaignId(value || null)}
          sx={{ minWidth: 260, justifyContent: "space-between" }}
        >
          {selectedCampaignLabel}
        </AppDropdown>
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
          Default view — touches counted across all campaigns and one-off broadcasts
        </Typography>
      </Box>

      {/* Summary cards */}
      {cardsError && !loadingCards && (
        <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error, mb: 1 }}>
          Couldn't load summary cards. Please refresh or try again.
        </Typography>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            label="Avg touches / contact"
            value={loadingCards ? "…" : formatCardValue(cards?.avg_touches_per_contact) ?? "—"}
            sx={SUMMARY_CARD_SX}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            label="Contacts, 10+ touches"
            value={loadingCards ? "…" : formatCardValue(cards?.contacts_10_plus) ?? "—"}
            sx={SUMMARY_CARD_SX}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            label="Contacts, 0 touches"
            value={loadingCards ? "…" : formatCardValue(cards?.contacts_zero_touches) ?? "—"}
            sx={SUMMARY_CARD_SX}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            label="List size"
            value={loadingCards ? "…" : formatCardValue(cards?.list_size) ?? "—"}
            sx={SUMMARY_CARD_SX}
          />
        </Grid>
      </Grid>

      {/* Distribution chart */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: "none", backgroundColor: "#F8F9FB" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography sx={{ ...typography.h2, mb: 2 }}>Touches per contact — distribution</Typography>
          <Box height={200}>
            {loadingChart ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <CircularProgress size={24} />
              </Box>
            ) : chartError ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <Typography sx={{ ...typography.bodyLarge, color: ColorsConstants.error }}>
                  Couldn't load the distribution chart. Please refresh or try again.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={orderedChartData}>
                  <CartesianGrid vertical={false} stroke="#F0F2F5" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 12, fill: grayFontColor }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: grayFontColor }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <RechartsTooltip />
                  <Bar dataKey="contact_count" name="Contacts" radius={[6, 6, 0, 0]}>
                    {orderedChartData.map((entry) => (
                      <Cell key={entry.bucket} fill={BUCKET_COLORS[entry.bucket]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Per-contact detail table */}
      <Card sx={{ borderRadius: 3, boxShadow: "none", backgroundColor: "#F8F9FB" }}>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mb: 2,
            }}
          >
            <Typography sx={{ ...typography.h2 }}>Per-contact detail</Typography>
            <Box sx={{ width: 260 }}>
              <TableSearchInput
                searchQuery={searchQuery}
                onSearchChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search phone number"
                error={Boolean(resolvedSearch.blockFetch && resolvedSearch.phoneSearchError)}
                helperText={resolvedSearch.blockFetch ? resolvedSearch.phoneSearchError : undefined}
              />
            </Box>
          </Box>

          <AppTable
            columns={columns}
            rows={tableRows}
            rowKey="phone"
            loading={loadingTable}
            emptyMessage="No contacts found for the selected filters."
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sortState={sortState}
            onSortChange={handleSortChange}
          />

          <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 1 }}>
            Server-side pagination (50–100 rows/page) + search-by-number + CSV export. Do not render full 50k+ list client-side.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReachFrequencyReport;
